import { useState } from 'react';

import { Box, Chip, Stack, Typography } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';

import PhoneFrame from '@/designs-dev/device/PhoneFrame';
import { northStarTheme } from '@/north-star/theme';
import PushScreen from '@/designs-dev/device/PushScreen';
import ModalScreen from '@/designs-dev/device/ModalScreen';
import LandingScreen from '@/north-star/LandingScreen';
import TriageScreen from '@/north-star/TriageScreen';
import RevealScreen from '@/north-star/RevealScreen';
import NorthStarShell from '@/north-star/NorthStarShell';
import FadeScreen from '@/north-star/FadeScreen';

// North Star — a fresh, standalone prototype of the AI-first journey, kept off
// the existing member-app shell on purpose. A persistent base screen (landing
// before onboarding, the home shell after) with overlays over it: the first
// "Talk to Keni" pushes in from the right; tapping the ever-present orb fades
// the app into the chat; the claim reveal slides up as a modal. Sandbox, like
// any prototype screen that isn't an exploration or a handover.
//
// An inspect bar on the stage jumps between stages and toggles autoplay, so the
// triage can be paused and stepped through.
type Stage = 'landing' | 'triage' | 'reveal' | 'home';

const STAGES: { id: Stage; label: string }[] = [
  { id: 'landing', label: 'Landing' },
  { id: 'triage', label: 'Triage' },
  { id: 'reveal', label: 'Reveal' },
  { id: 'home', label: 'Home' },
];

const NorthStarApp = () => {
  const [onboarded, setOnboarded] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [revealOpen, setRevealOpen] = useState(false);
  // Bumped to remount the chat fresh each time it's opened.
  const [chatKey, setChatKey] = useState(0);
  // Orb taps fade the app into the chat; the first "Talk to Keni" pushes in.
  const [chatFade, setChatFade] = useState(false);
  const [autoPlay, setAutoPlay] = useState(true);

  const openChat = (fade = false) => {
    setChatFade(fade);
    setChatKey((k) => k + 1);
    setRevealOpen(false);
    setChatOpen(true);
  };

  const stage: Stage = revealOpen
    ? 'reveal'
    : chatOpen
      ? 'triage'
      : onboarded
        ? 'home'
        : 'landing';

  const goStage = (s: Stage) => {
    setRevealOpen(s === 'reveal');
    setChatOpen(s === 'triage' || s === 'reveal');
    setOnboarded(s === 'home');
    if (s === 'triage' || s === 'reveal') setChatKey((k) => k + 1);
  };

  return (
    // Sized by its own content, and painting no background. Both were the other
    // way round when this was the whole page, and both were wrong once the
    // chrome went around it: 100vh under a header overflows and pushes the phone
    // down the screen, a background on a centred child covers the phone's width
    // and nothing else — the pale column down the middle of a different colour —
    // and growing to fill the stage pushed the note underneath away from the
    // thing it is a note about. One background, the Loft page's; the frame is
    // the only surface on it; and ScreenChrome centres this and the note
    // together as one group.
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
      }}
    >
      <ThemeProvider theme={northStarTheme}>
        <PhoneFrame>
          {/* base */}
          {onboarded ? (
            <NorthStarShell
              onAskKeni={() => openChat(true)}
              onSeeBreakdown={() => setRevealOpen(true)}
            />
          ) : (
            <LandingScreen onStart={() => openChat(false)} />
          )}

          {/* Talk to Keni — the first entry pushes in from the right; an orb tap
            fades the app into the chat */}
          {chatOpen &&
            (chatFade ? (
              <FadeScreen key={chatKey} onClose={() => setChatOpen(false)}>
                {(close) => (
                  <TriageScreen
                    autoPlay={autoPlay}
                    onComplete={() => setRevealOpen(true)}
                    onExit={close}
                  />
                )}
              </FadeScreen>
            ) : (
              <PushScreen key={chatKey} onClose={() => setChatOpen(false)}>
                {(close) => (
                  <TriageScreen
                    autoPlay={autoPlay}
                    onComplete={() => setRevealOpen(true)}
                    onExit={close}
                  />
                )}
              </PushScreen>
            ))}

          {/* the claim reveal — slides up as a modal */}
          {revealOpen && (
            <ModalScreen onClose={() => setRevealOpen(false)}>
              {(close) => (
                <RevealScreen
                  onContinue={() => {
                    setOnboarded(true);
                    setChatOpen(false);
                    close();
                  }}
                  onClose={() => {
                    setOnboarded(true);
                    setChatOpen(false);
                    close();
                  }}
                  onAskKeni={() => {
                    setChatFade(true);
                    setChatKey((k) => k + 1);
                    setChatOpen(true);
                    close();
                  }}
                />
              )}
            </ModalScreen>
          )}
        </PhoneFrame>
      </ThemeProvider>

      {/* The stage bar, under the frame. It reads as the controls for the phone
          above it rather than as a heading over the whole stage, and it leaves
          the top of the screen to the exploration select — which is the one
          control that changes what you are looking at rather than where in it
          you are. */}
      {/* inspect bar — jump to any stage, pause autoplay to step the triage */}
      <Stack
        sx={{
          alignItems: 'center',
          flexWrap: 'wrap',
          justifyContent: 'center',
        }}
        direction='row'
        spacing={1}
        useFlexGap
      >
        <Typography sx={{ mr: 0.5 }} variant='overline' color='text.secondary'>
          Stage
        </Typography>
        {STAGES.map((s) => (
          <Chip
            key={s.id}
            label={s.label}
            size='small'
            clickable
            color={stage === s.id ? 'primary' : 'default'}
            variant={stage === s.id ? 'filled' : 'outlined'}
            onClick={() => goStage(s.id)}
          />
        ))}
        <Chip
          label={autoPlay ? 'Autoplay: on' : 'Autoplay: off'}
          size='small'
          clickable
          color={autoPlay ? 'primary' : 'default'}
          variant={autoPlay ? 'filled' : 'outlined'}
          onClick={() => setAutoPlay((v) => !v)}
        />
      </Stack>
    </Box>
  );
};

NorthStarApp.displayName = 'NorthStarApp';
export default NorthStarApp;
